/**
 * ConnectionStatusPage Component
 * Admin-only diagnostic page showing the health of all external service connections.
 * Displays endpoint URIs, connectivity status, latency, and service-specific details.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Loader2,
  Database,
  Upload,
  FileText,
  Search,
  Package,
  AlertTriangle,
} from 'lucide-react';
import { BRRApiClient } from '../services';
import { ServiceStatus, ConnectionStatusResponse } from '../services/types';

interface ConnectionStatusPageProps {
  apiClient: BRRApiClient;
  isActive?: boolean;
}

const SERVICE_META: Record<string, { label: string; icon: React.ComponentType<any> }> = {
  neo4j:        { label: 'Neo4j Graph Database', icon: Database },
  blob_storage: { label: 'Azure Blob Storage',   icon: Upload },
  box:          { label: 'Box Document Storage',  icon: FileText },
  openai:       { label: 'Azure OpenAI / Foundry', icon: Search },
  tracelink:    { label: 'TraceLink SOAP API',   icon: Package },
};

const DetailRow = ({ label, value }: { label: string; value: string | number | null | undefined }) => {
  if (value === null || value === undefined) return null;
  const strValue = String(value);
  return (
    <div className="flex justify-between text-xs py-0.5 gap-2 min-w-0">
      <span className="text-gray-500 flex-shrink-0">{label}</span>
      <span className="text-gray-700 font-medium truncate text-right" title={strValue}>
        {strValue}
      </span>
    </div>
  );
};

const ServiceCard = ({ service }: { service: ServiceStatus }) => {
  const meta = SERVICE_META[service.service] || { label: service.service, icon: Database };
  const Icon = meta.icon;
  const isConnected = service.status === 'connected';

  // Format detail keys for display
  const formatLabel = (key: string): string =>
    key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 flex flex-col gap-3 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded ${isConnected ? 'bg-green-50' : 'bg-red-50'}`}>
            <Icon size={18} className={isConnected ? 'text-green-600' : 'text-red-500'} />
          </div>
          <span className="text-sm font-semibold text-gray-900">{meta.label}</span>
        </div>
        {isConnected
          ? <CheckCircle size={20} className="text-green-500" data-testid={`status-ok-${service.service}`} />
          : <AlertCircle size={20} className="text-red-500" data-testid={`status-error-${service.service}`} />
        }
      </div>

      {/* Endpoint */}
      {service.endpoint && (
        <div>
          <div className="text-xs text-gray-500 mb-0.5">Endpoint</div>
          <code
            className="text-xs bg-gray-50 text-gray-800 px-2 py-1 rounded block truncate"
            title={service.endpoint}
          >
            {service.endpoint}
          </code>
        </div>
      )}

      {/* Latency */}
      {service.latency_ms !== null && service.latency_ms !== undefined && (
        <DetailRow label="Latency" value={`${service.latency_ms}ms`} />
      )}

      {/* Service-specific details */}
      {service.details && Object.keys(service.details).length > 0 && (
        <div className="border-t border-gray-100 pt-2">
          {Object.entries(service.details).map(([key, val]) => {
            const display = Array.isArray(val) ? val.join(', ') : val;
            return <DetailRow key={key} label={formatLabel(key)} value={display} />;
          })}
        </div>
      )}

      {/* Error */}
      {service.error && (
        <div className="bg-red-50 border border-red-200 rounded p-2 mt-auto">
          <div className="flex items-start gap-1.5">
            <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
            <span className="text-xs text-red-700 break-all">{service.error}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const ConnectionStatusPageComponent: React.FC<ConnectionStatusPageProps> = ({ apiClient, isActive }) => {
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data: ConnectionStatusResponse = await apiClient.getConnectionStatus();
      setServices(data.services);
      setLastChecked(data.timestamp);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch connection status');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const connectedCount = services.filter(s => s.status === 'connected').length;
  const totalCount = services.length;

  return (
    <div className="flex-1 overflow-y-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Connection Status</h1>
          <p className="text-sm text-gray-500 mt-1">
            {loading
              ? 'Checking connections...'
              : `${connectedCount} of ${totalCount} services connected`
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastChecked && (
            <span className="text-xs text-gray-400">
              Last checked: {new Date(lastChecked).toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchStatus}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            data-testid="refresh-button"
          >
            {loading
              ? <Loader2 size={14} className="animate-spin" />
              : <RefreshCw size={14} />
            }
            Refresh
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6 flex items-start gap-2" data-testid="error-banner">
          <AlertTriangle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-red-800">Failed to load connection status</div>
            <div className="text-xs text-red-600 mt-0.5">{error}</div>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && services.length === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 animate-pulse">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-gray-200 rounded" />
                <div className="h-4 w-32 bg-gray-200 rounded" />
              </div>
              <div className="h-3 w-48 bg-gray-100 rounded mb-2" />
              <div className="h-3 w-24 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      )}

      {/* Service cards */}
      {services.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map(service => (
            <ServiceCard key={service.service} service={service} />
          ))}
        </div>
      )}
    </div>
  );
};

export const ConnectionStatusPage = React.memo(ConnectionStatusPageComponent);
